import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  ownedShares: [], // Shares where current user is the owner
  receivedShares: [], // Shares where current user has been granted access
  pendingInvitations: [], // Invitations sent by current user
  receivedInvitations: [], // Invitations received by current user
  sharedChildren: [], // Children shared with current user
  isLoading: false,
  error: null,
  selectedChild: null,
  filters: {
    status: 'all', // 'all', 'active', 'pending', 'expired'
    permission: 'all', // 'all', 'view', 'edit', 'admin'
    sortBy: 'created_at', // 'created_at', 'child_name', 'email', 'status'
    sortOrder: 'desc', // 'asc', 'desc'
  },
  pagination: {
    page: 1,
    pageSize: 10,
    total: 0,
    hasMore: false,
  },
  realTimeUpdates: {
    lastUpdate: null,
    pendingUpdates: [],
  },
};

const sharingSlice = createSlice({
  name: 'sharing',
  initialState,
  reducers: {
    // Data management
    setOwnedShares: (state, action) => {
      state.ownedShares = action.payload;
    },
    setReceivedShares: (state, action) => {
      state.receivedShares = action.payload;
    },
    setPendingInvitations: (state, action) => {
      state.pendingInvitations = action.payload;
    },
    setReceivedInvitations: (state, action) => {
      state.receivedInvitations = action.payload;
    },
    setSharedChildren: (state, action) => {
      state.sharedChildren = action.payload;
    },

    // Individual item updates
    addOwnedShare: (state, action) => {
      state.ownedShares.push(action.payload);
    },
    updateOwnedShare: (state, action) => {
      const { id, updates } = action.payload;
      const index = state.ownedShares.findIndex(share => share.id === id);
      if (index !== -1) {
        state.ownedShares[index] = { ...state.ownedShares[index], ...updates };
      }
    },
    removeOwnedShare: (state, action) => {
      const id = action.payload;
      state.ownedShares = state.ownedShares.filter(share => share.id !== id);
    },

    addReceivedShare: (state, action) => {
      state.receivedShares.push(action.payload);
    },
    updateReceivedShare: (state, action) => {
      const { id, updates } = action.payload;
      const index = state.receivedShares.findIndex(share => share.id === id);
      if (index !== -1) {
        state.receivedShares[index] = { ...state.receivedShares[index], ...updates };
      }
    },
    removeReceivedShare: (state, action) => {
      const id = action.payload;
      state.receivedShares = state.receivedShares.filter(share => share.id !== id);
    },

    addPendingInvitation: (state, action) => {
      state.pendingInvitations.push(action.payload);
    },
    updatePendingInvitation: (state, action) => {
      const { id, updates } = action.payload;
      const index = state.pendingInvitations.findIndex(inv => inv.id === id);
      if (index !== -1) {
        state.pendingInvitations[index] = { ...state.pendingInvitations[index], ...updates };
      }
    },
    removePendingInvitation: (state, action) => {
      const id = action.payload;
      state.pendingInvitations = state.pendingInvitations.filter(inv => inv.id !== id);
    },

    addReceivedInvitation: (state, action) => {
      state.receivedInvitations.push(action.payload);
    },
    updateReceivedInvitation: (state, action) => {
      const { id, updates } = action.payload;
      const index = state.receivedInvitations.findIndex(inv => inv.id === id);
      if (index !== -1) {
        state.receivedInvitations[index] = { ...state.receivedInvitations[index], ...updates };
      }
    },
    removeReceivedInvitation: (state, action) => {
      const id = action.payload;
      state.receivedInvitations = state.receivedInvitations.filter(inv => inv.id !== id);
    },

    // Child selection
    setSelectedChild: (state, action) => {
      state.selectedChild = action.payload;
    },
    clearSelectedChild: (state) => {
      state.selectedChild = null;
    },

    // Filtering and sorting
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    setFilter: (state, action) => {
      const { key, value } = action.payload;
      state.filters[key] = value;
    },
    resetFilters: (state) => {
      state.filters = initialState.filters;
    },
    setSortBy: (state, action) => {
      const { sortBy, sortOrder } = action.payload;
      state.filters.sortBy = sortBy;
      state.filters.sortOrder = sortOrder || 'desc';
    },

    // Pagination
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    setPage: (state, action) => {
      state.pagination.page = action.payload;
    },
    resetPagination: (state) => {
      state.pagination = initialState.pagination;
    },

    // Loading and error states
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
      state.isLoading = false;
    },
    clearError: (state) => {
      state.error = null;
    },

    // Real-time updates
    addRealTimeUpdate: (state, action) => {
      state.realTimeUpdates.pendingUpdates.push(action.payload);
      state.realTimeUpdates.lastUpdate = new Date().toISOString();
    },
    processRealTimeUpdate: (state, action) => {
      const { type, data } = action.payload;
      
      switch (type) {
        case 'invitation_sent':
          state.pendingInvitations.push(data);
          break;
        case 'invitation_received':
          state.receivedInvitations.push(data);
          break;
        case 'invitation_accepted':
          // Move from pending to owned shares
          state.pendingInvitations = state.pendingInvitations.filter(inv => inv.id !== data.id);
          state.ownedShares.push(data);
          break;
        case 'invitation_declined':
          state.pendingInvitations = state.pendingInvitations.filter(inv => inv.id !== data.id);
          break;
        case 'share_revoked':
          state.ownedShares = state.ownedShares.filter(share => share.id !== data.id);
          state.receivedShares = state.receivedShares.filter(share => share.id !== data.id);
          break;
        case 'permission_updated': {
          const ownedIndex = state.ownedShares.findIndex(share => share.id === data.id);
          if (ownedIndex !== -1) {
            state.ownedShares[ownedIndex] = { ...state.ownedShares[ownedIndex], ...data };
          }
          const receivedIndex = state.receivedShares.findIndex(share => share.id === data.id);
          if (receivedIndex !== -1) {
            state.receivedShares[receivedIndex] = { ...state.receivedShares[receivedIndex], ...data };
          }
          break;
        }
        default:
          break;
      }
      
      state.realTimeUpdates.lastUpdate = new Date().toISOString();
    },
    clearRealTimeUpdates: (state) => {
      state.realTimeUpdates.pendingUpdates = [];
    },

    // Reset sharing state
    resetSharing: (_state) => {
      return { ...initialState };
    },

    // Async action handlers
    fetchSharingDataStart: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    fetchSharingDataSuccess: (state, action) => {
      const { ownedShares, receivedShares, pendingInvitations, receivedInvitations, sharedChildren, pagination } = action.payload;
      state.ownedShares = ownedShares || [];
      state.receivedShares = receivedShares || [];
      state.pendingInvitations = pendingInvitations || [];
      state.receivedInvitations = receivedInvitations || [];
      state.sharedChildren = sharedChildren || [];
      if (pagination) {
        state.pagination = { ...state.pagination, ...pagination };
      }
      state.isLoading = false;
      state.error = null;
    },
    fetchSharingDataFailure: (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    },

    sendInvitationStart: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    sendInvitationSuccess: (state, action) => {
      state.pendingInvitations.push(action.payload);
      state.isLoading = false;
      state.error = null;
    },
    sendInvitationFailure: (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    },

    acceptInvitationStart: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    acceptInvitationSuccess: (state, action) => {
      const { invitationId, shareData } = action.payload;
      // Remove from received invitations
      state.receivedInvitations = state.receivedInvitations.filter(inv => inv.id !== invitationId);
      // Add to received shares
      state.receivedShares.push(shareData);
      state.isLoading = false;
      state.error = null;
    },
    acceptInvitationFailure: (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    },

    declineInvitationStart: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    declineInvitationSuccess: (state, action) => {
      const invitationId = action.payload;
      state.receivedInvitations = state.receivedInvitations.filter(inv => inv.id !== invitationId);
      state.isLoading = false;
      state.error = null;
    },
    declineInvitationFailure: (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    },

    revokeShareStart: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    revokeShareSuccess: (state, action) => {
      const shareId = action.payload;
      state.ownedShares = state.ownedShares.filter(share => share.id !== shareId);
      state.isLoading = false;
      state.error = null;
    },
    revokeShareFailure: (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    },

    updatePermissionStart: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    updatePermissionSuccess: (state, action) => {
      const { shareId, permission } = action.payload;
      const index = state.ownedShares.findIndex(share => share.id === shareId);
      if (index !== -1) {
        state.ownedShares[index].permission = permission;
      }
      state.isLoading = false;
      state.error = null;
    },
    updatePermissionFailure: (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    },
  },
});

export const {
  // Data management
  setOwnedShares,
  setReceivedShares,
  setPendingInvitations,
  setReceivedInvitations,
  setSharedChildren,
  
  // Individual updates
  addOwnedShare,
  updateOwnedShare,
  removeOwnedShare,
  addReceivedShare,
  updateReceivedShare,
  removeReceivedShare,
  addPendingInvitation,
  updatePendingInvitation,
  removePendingInvitation,
  addReceivedInvitation,
  updateReceivedInvitation,
  removeReceivedInvitation,
  
  // Child selection
  setSelectedChild,
  clearSelectedChild,
  
  // Filtering and sorting
  setFilters,
  setFilter,
  resetFilters,
  setSortBy,
  
  // Pagination
  setPagination,
  setPage,
  resetPagination,
  
  // Loading and errors
  setLoading,
  setError,
  clearError,
  
  // Real-time updates
  addRealTimeUpdate,
  processRealTimeUpdate,
  clearRealTimeUpdates,
  
  // Reset
  resetSharing,
  
  // Async actions
  fetchSharingDataStart,
  fetchSharingDataSuccess,
  fetchSharingDataFailure,
  sendInvitationStart,
  sendInvitationSuccess,
  sendInvitationFailure,
  acceptInvitationStart,
  acceptInvitationSuccess,
  acceptInvitationFailure,
  declineInvitationStart,
  declineInvitationSuccess,
  declineInvitationFailure,
  revokeShareStart,
  revokeShareSuccess,
  revokeShareFailure,
  updatePermissionStart,
  updatePermissionSuccess,
  updatePermissionFailure,
} = sharingSlice.actions;

// Selectors
export const selectSharingState = (state) => state.sharing;
export const selectOwnedShares = (state) => state.sharing.ownedShares;
export const selectReceivedShares = (state) => state.sharing.receivedShares;
export const selectPendingInvitations = (state) => state.sharing.pendingInvitations;
export const selectReceivedInvitations = (state) => state.sharing.receivedInvitations;
export const selectSharedChildren = (state) => state.sharing.sharedChildren;
export const selectSelectedChild = (state) => state.sharing.selectedChild;
export const selectIsLoading = (state) => state.sharing.isLoading;
export const selectError = (state) => state.sharing.error;
export const selectFilters = (state) => state.sharing.filters;
export const selectPagination = (state) => state.sharing.pagination;
export const selectRealTimeUpdates = (state) => state.sharing.realTimeUpdates;

// Computed selectors
export const selectFilteredOwnedShares = (state) => {
  const { ownedShares, filters } = state.sharing;
  let filtered = [...ownedShares];
  
  // Filter by status
  if (filters.status !== 'all') {
    filtered = filtered.filter(share => share.status === filters.status);
  }
  
  // Filter by permission
  if (filters.permission !== 'all') {
    filtered = filtered.filter(share => share.permission === filters.permission);
  }
  
  // Sort
  filtered.sort((a, b) => {
    const aValue = a[filters.sortBy];
    const bValue = b[filters.sortBy];
    const comparison = aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    return filters.sortOrder === 'asc' ? comparison : -comparison;
  });
  
  return filtered;
};

export const selectFilteredReceivedShares = (state) => {
  const { receivedShares, filters } = state.sharing;
  let filtered = [...receivedShares];
  
  // Filter by status
  if (filters.status !== 'all') {
    filtered = filtered.filter(share => share.status === filters.status);
  }
  
  // Filter by permission
  if (filters.permission !== 'all') {
    filtered = filtered.filter(share => share.permission === filters.permission);
  }
  
  // Sort
  filtered.sort((a, b) => {
    const aValue = a[filters.sortBy];
    const bValue = b[filters.sortBy];
    const comparison = aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    return filters.sortOrder === 'asc' ? comparison : -comparison;
  });
  
  return filtered;
};

export const selectTotalShares = (state) => {
  return state.sharing.ownedShares.length + state.sharing.receivedShares.length;
};

export const selectTotalInvitations = (state) => {
  return state.sharing.pendingInvitations.length + state.sharing.receivedInvitations.length;
};

export const selectShareByChildId = (childId) => (state) => {
  return state.sharing.ownedShares.find(share => share.child_id === childId) ||
         state.sharing.receivedShares.find(share => share.child_id === childId);
};

export const selectHasPendingUpdates = (state) => {
  return state.sharing.realTimeUpdates.pendingUpdates.length > 0;
};

export default sharingSlice.reducer;